import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useTrackSetCustomBoardThumbnail } from "@air/analytics";
import { Button } from "@air/primitive-button";
import { Modal, ModalCloseButton, ModalDescription, ModalTitle } from "@air/primitive-modal";
import { useToasts } from "@air/provider-toast";
import { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { PrivateBoardSearch } from "~/components/BoardSearch/PrivateBoardSearch";
import { useSingleBoardSearchState } from "~/components/BoardSearch/shared/hooks";
import { ToastLink } from "~/components/UI/ToastLink";
import { useBoardPermissionsCache } from "~/hooks/useBoardPermissionsCache";
import { useGoToBoardPage } from "~/hooks/useGoToBoardPage";
import { useShowSubscriptionExpiredModal } from "~/hooks/useShowSubscriptionExpiredModal";
import { setCentralizedBoardAction } from "~/store/centralizedBoard/actions";
import { centralizedBoardSelector } from "~/store/centralizedBoard/selectors";
import { updateCentralizedClipBoardThumbnails } from "~/store/centralizedClip/actions";
import { useSetBoardThumbnail } from "~/swr-hooks/boards/useSetBoardThumbnail";
import { canChangeBoardDetails } from "~/utils/permissions/boardPermissions";
export const SetBoardThumbnailModal = (param)=>{
    let { onClose, trackLocation, clip } = param;
    const dispatch = useDispatch();
    const { goToBoardPage } = useGoToBoardPage();
    const { setBoardThumbnail } = useSetBoardThumbnail();
    const { showToast } = useToasts();
    const currentBoard = useSelector(centralizedBoardSelector);
    const [isAddingThumbnail, setIsAddingThumbnail] = useState(false);
    const { showingSubscriptionExpiredModal } = useShowSubscriptionExpiredModal({
        onClose
    });
    const { trackSetCustomBoardThumbnail } = useTrackSetCustomBoardThumbnail();
    const { getBoardPermissions } = useBoardPermissionsCache();
    const { selectedBoard, isBoardSelected, shouldShowSubBoards, onSelectBoard, isLibrarySelected, shouldShowLibraryBoards, onSelectLibrary } = useSingleBoardSearchState({
        initialBoard: currentBoard
    });
    const onBoardSelectChange = useCallback((param)=>{
        let { board, selected } = param;
        onSelectBoard(selected ? board : null);
    }, [
        onSelectBoard
    ]);
    const getIsBoardDisabled = useCallback((board)=>{
        const boardPermissions = getBoardPermissions(board.id);
        if (!canChangeBoardDetails(boardPermissions)) {
            return {
                isDisabled: true,
                message: "You do not have permission to edit this board"
            };
        }
    }, [
        getBoardPermissions
    ]);
    if (showingSubscriptionExpiredModal) {
        return null;
    }
    const goToBoard = (board)=>{
        goToBoardPage({
            board,
            trackLocation: "set-custom-board-thumbnail-toast"
        });
    };
    const processBoardThumbnail = async (selectedBoard)=>{
        const isSelectedBoardCurrentBoard = (currentBoard === null || currentBoard === void 0 ? void 0 : currentBoard.id) === selectedBoard.id;
        try {
            const newThumbnailBoard = await setBoardThumbnail({
                boardId: selectedBoard.id,
                clip
            });
            if (!isSelectedBoardCurrentBoard) {
                dispatch(updateCentralizedClipBoardThumbnails({
                    boardId: selectedBoard.id,
                    newThumbnail: clip.assets.image
                }));
            } else if (!!newThumbnailBoard) {
                dispatch(setCentralizedBoardAction({
                    board: newThumbnailBoard
                }));
            }
        } catch (e) {
        // TODO: we should probably have ui to handle this case
        }
        return selectedBoard;
    };
    const isUpdatingCurrentBoard = (board)=>board.id === (currentBoard === null || currentBoard === void 0 ? void 0 : currentBoard.id);
    const logCustomBoardThumbnailEvent = (param)=>{
        let { board } = param;
        trackSetCustomBoardThumbnail({
            clipId: clip.id,
            boardId: board.id,
            setCurrentBoardThumbnail: isUpdatingCurrentBoard(board),
            location: trackLocation
        });
    };
    const showConfirmationToast = (board)=>{
        showToast(/*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                !isUpdatingCurrentBoard(board) ? /*#__PURE__*/ _jsx(ToastLink, {
                    onClick: ()=>goToBoard(board),
                    children: board.title
                }) : board.title,
                " thumbnail has been updated"
            ]
        }));
    };
    const handleAddClick = async ()=>{
        if (!selectedBoard) {
            throw new Error("No board selected");
        }
        setIsAddingThumbnail(true);
        try {
            const board = await processBoardThumbnail(selectedBoard);
            if (board) {
                showConfirmationToast(board);
                logCustomBoardThumbnailEvent({
                    board
                });
            }
            onClose();
        } catch (_error) {
            setIsAddingThumbnail(false);
            showToast("An error occurred - Please try again later");
        }
    };
    return /*#__PURE__*/ _jsxs(Modal, {
        "data-testid": "SET_BOARD_THUMBNAIL_MODAL",
        dangerouslyBypassFocusLock: true,
        isOpen: true,
        onDismiss: onClose,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "mb-3 flex justify-between",
                children: [
                    /*#__PURE__*/ _jsx(ModalTitle, {
                        children: "Set as board thumbnail"
                    }),
                    /*#__PURE__*/ _jsx(ModalCloseButton, {
                        className: "shrink-0",
                        onClick: onClose
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(ModalDescription, {
                className: "mb-4",
                children: "Choose a board to update."
            }),
            /*#__PURE__*/ _jsx(PrivateBoardSearch, {
                isBoardSelected: isBoardSelected,
                onBoardSelectChange: onBoardSelectChange,
                shouldShowSubBoards: shouldShowSubBoards,
                initialBoardId: currentBoard === null || currentBoard === void 0 ? void 0 : currentBoard.id,
                isLibrarySelected: isLibrarySelected,
                shouldShowLibraryBoards: shouldShowLibraryBoards,
                onSelectLibrary: onSelectLibrary,
                shouldExpandLibraryInsteadOfSelect: true,
                getIsBoardDisabled: getIsBoardDisabled
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "mt-6 flex justify-end gap-3",
                children: [
                    /*#__PURE__*/ _jsx(Button, {
                        appearance: "ghost",
                        color: "grey",
                        onClick: onClose,
                        size: "large",
                        children: "Cancel"
                    }),
                    /*#__PURE__*/ _jsx(Button, {
                        appearance: "filled",
                        color: "blue",
                        disabled: isAddingThumbnail || !selectedBoard,
                        size: "large",
                        onClick: ()=>handleAddClick(),
                        children: isAddingThumbnail ? "Applying…" : "Apply"
                    })
                ]
            })
        ]
    });
};
